
import * as React from "react";

import type { Omittable } from "../lib/props.ts";

const DEFAULT_LIMIT = 50;

export interface SnapshotHistory {
  snapshot: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

// Never owns the value; call snapshot() immediately before a host edit (clears redo).
export function useSnapshotHistory<T>(
  options: Omittable<{
    value: T;
    commit: (next: T) => void;
    resetKey: string;
    limit?: number;
    clone?: (value: T) => T;
    isEqual?: (a: T, b: T) => boolean;
  }>,
): SnapshotHistory {
  const { value, commit, resetKey, limit = DEFAULT_LIMIT, clone, isEqual } = options;

  const valueRef = React.useRef(value);
  const commitRef = React.useRef(commit);
  const cloneRef = React.useRef(clone);
  const isEqualRef = React.useRef(isEqual ?? Object.is);
  React.useLayoutEffect(() => {
    valueRef.current = value;
    commitRef.current = commit;
    cloneRef.current = clone;
    isEqualRef.current = isEqual ?? Object.is;
  });

  const pastRef = React.useRef<T[]>([]);
  const futureRef = React.useRef<T[]>([]);
  const [flags, setFlags] = React.useState({ canUndo: false, canRedo: false });

  // A different key is a different document: its snapshots must not leak across.
  const resetKeyRef = React.useRef(resetKey);
  if (resetKey !== resetKeyRef.current) {
    resetKeyRef.current = resetKey;
    pastRef.current = [];
    futureRef.current = [];
    setFlags((prev) =>
      prev.canUndo || prev.canRedo ? { canUndo: false, canRedo: false } : prev,
    );
  }

  const syncFlags = React.useCallback(() => {
    const next = {
      canUndo: pastRef.current.length > 0,
      canRedo: futureRef.current.length > 0,
    };
    setFlags((prev) =>
      prev.canUndo === next.canUndo && prev.canRedo === next.canRedo ? prev : next,
    );
  }, []);

  const current = React.useCallback(
    () => (cloneRef.current ? cloneRef.current(valueRef.current) : valueRef.current),
    [],
  );

  const snapshot = React.useCallback(() => {
    pastRef.current.push(current());
    if (pastRef.current.length > limit) pastRef.current.shift();
    futureRef.current = [];
    syncFlags();
  }, [current, limit, syncFlags]);

  const popChange = React.useCallback((stack: T[]) => {
    let snap = stack.pop();
    while (snap !== undefined && isEqualRef.current(snap, valueRef.current)) snap = stack.pop();
    return snap;
  }, []);

  const undo = React.useCallback(() => {
    const snap = popChange(pastRef.current);
    if (snap === undefined) {
      syncFlags();
      return;
    }
    futureRef.current.push(current());
    syncFlags();
    commitRef.current(snap);
  }, [current, popChange, syncFlags]);

  const redo = React.useCallback(() => {
    const snap = popChange(futureRef.current);
    if (snap === undefined) {
      syncFlags();
      return;
    }
    pastRef.current.push(current());
    syncFlags();
    commitRef.current(snap);
  }, [current, popChange, syncFlags]);

  return React.useMemo(
    () => ({ snapshot, undo, redo, canUndo: flags.canUndo, canRedo: flags.canRedo }),
    [snapshot, undo, redo, flags.canUndo, flags.canRedo],
  );
}
